import React, {useLayoutEffect} from "react";
import _ from "lodash";

import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5locales_it_IT from "@amcharts/amcharts5/locales/it_IT"
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import {getXYChart, addSeriesBullet, addLicense} from "../../../components/Charts/commons";

export const COLORS = {
  promoter: am5.color("#87ad84"),
  adopter: am5.color("#047857"),
  user: am5.color("rgb(0, 54, 70)"),
  activated: am5.color("rgb(11,79,100)"),
  registered: am5.color("#ca8a04"),
  churn: am5.color("#b91c1c"),
  pending: am5.color("#dca024"),
  never_registered: am5.color("#262d38"),
  leaving: am5.color("#4f5c70")
}

function UsersTrendChart({id, data}) {

  useLayoutEffect(() => {

    const statusList = (data || []).length > 0 ?
      Object.keys(data[0]).filter(k => k.startsWith('val_'))
        .map(k => k.replace('val_', '')) : []

    addLicense();
    let root = am5.Root.new(id);
    root.setThemes([am5themes_Animated.new(root)])
    root.locale = am5locales_it_IT;
    root.numberFormatter.set("numberFormat", "#,###.");
    root.dateFormatter.set("dateFormat", "MMM yyyy")

    // Create chart
    let chart = getXYChart(root, {layout: root.verticalLayout})
    let cursor = chart.set("cursor", am5xy.XYCursor.new(root, {}));
    cursor.lineX.set("visible", false);
    cursor.lineY.set("visible", false);

    let legend = chart.children.push(
      am5.Legend.new(root, {
        centerX: am5.p50,
        x: am5.p50
      })
    );

    legend.markers.template.setAll({
      width: 10,
      height: 10
    });

    legend.labels.template.setAll({
      fontSize: 10,
      fontWeight: "300"
    });

    // Create axes
    let xAxis = chart.xAxes.push(
      chart.xAxes.push(am5xy.DateAxis.new(root, {
          baseInterval: {timeUnit: 'month', count: 1},
          gridIntervals: [{timeUnit: "month", count: 1}],
          renderer: am5xy.AxisRendererX.new(root, {}),
        })
      ))
    xAxis.data.setAll(data);
    let xRenderer = xAxis.get("renderer");
    xRenderer.labels.template.set("fontSize", 12);

    let yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
      min: 0,
      extraMax: 0.1,
      renderer: am5xy.AxisRendererY.new(root, {}),
      calculateTotals: true
    }))
    let yRenderer = yAxis.get("renderer");
    yRenderer.grid.template.set("forceHidden", true);
    yRenderer.labels.template.set("forceHidden", true);
    yAxis.data.setAll(data);

    // Add series
    function createSeries(name) {
      let series = chart.series.push(am5xy.LineSeries.new(root, {
        name: name,
        xAxis: xAxis,
        yAxis: yAxis,
        stacked: true,
        fill: COLORS[name],
        valueYField: `val_${name}`,
        valueXField: "month",
        tooltip: am5.Tooltip.new(root, {
          labelText: "{name}: [bold]{valueY}[/]",
          tooltipY: am5.percent(10)
        })
      }))

      series.fills.template.setAll({
        fillOpacity: 0.65,
        visible: true
      });

      // Add bullet
      addSeriesBullet(root, series, 3, 0)

      if (name === statusList[statusList.length - 1]) {
        series.bullets.push(function () {
          return am5.Bullet.new(root, {
            locationY: 1,
            sprite: am5.Label.new(root, {
              text: "[bold]{valueYTotal}[/]",
              centerX: am5.p50,
              centerY: am5.percent(85),
              populateText: true,
              fontSize: 10
            })
          });
        });
      }

      series.data.processor = am5.DataProcessor.new(root, {
        dateFields: ['month'],
        dateFormat: "yyyy-MM-dd"
      })
      series.data.setAll(data);
      series.appear(1000);

      legend.data.push(series);
    }

    statusList.forEach(s => {
      createSeries(s)
    })

    return () => {
      root.dispose()
    }
  }, [data, id]) // eslint-disable-line react-hooks/exhaustive-deps

  return <div id={id} className="w-full h-96"/>
}

export default React.memo(UsersTrendChart, (props, nextProps) => {
  return _.isEqual(props.data, nextProps.data)
})
